import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { AttachmentsCard } from '../../components/AttachmentsCard';
import { NOTIFICATIONS_KEY, useNotifications } from '../../components/NotificationsBell';
import { Alert, Badge, Button, Card, Select, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import {
  appLabels,
  formatDateTime,
  type Nature,
  NATURES,
  natureName,
  type Sla,
  statusTone,
  type TicketDetail,
  type TicketFormData,
  useTicket,
  useTicketForm,
} from './data';
import { UsedArticlesCard } from '../knowledge/ArticleLinks';
import { Conversation } from './Conversation';
import { describeSla, SlaBar } from './sla';
import { SurveyCard } from './SurveyCard';
import { TakeButton } from './TakeButton';
import { PriorityLabel } from './TicketsPage';
import { useAction } from './useAction';

/** Detalle de un ticket (guía 7.1): lo que pidió el solicitante, el SLA y, para IT, el triage y el estado. */
export function TicketDetailPage() {
  const { number = '' } = useParams();
  const me = useMe();
  const location = useLocation();
  const ticket = useTicket(number);
  const form = useTicketForm();
  useReadTicketNotifications(number);

  if (!ticket.data) return ticket.error ? <Alert>{errorMessage(ticket.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const tk = ticket.data;
  const created = (location.state as { created?: boolean } | null)?.created;
  const isRequester = tk.requesterId === me.id;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/tickets" className="text-sm text-marca hover:underline">
          ← {t('tk.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          <span className="mr-2 font-mono text-base text-slate-500">{tk.code}</span>
          {tk.title}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={statusTone(tk.statusCategory)}>{tk.status}</Badge>
          {tk.priority && <PriorityLabel name={tk.priority} color={tk.priorityColor} />}
          <span className="text-slate-500">· {natureName(tk.nature)}</span>
        </div>
      </div>
      {created && <Alert tone="success">{t('tk.creado', { codigo: tk.code })}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          <SurveyCard ticket={tk} />
          <Card title={t('tk.descripcion_titulo')}>
            <p className="whitespace-pre-wrap text-sm text-slate-800">{tk.description}</p>
          </Card>
          <Conversation ticket={tk} />
        </div>
        <div className="space-y-4">
          <SlaCard sla={tk.sla} />
          <Card variant="aside" title={t('tk.datos')}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-slate-500">{t('tk.solicitante')}</dt>
              <dd>{tk.requester ?? '—'}</dd>
              <dt className="text-slate-500">{t('tk.responsable')}</dt>
              <dd>{tk.assignee ?? <span className="text-slate-400">{t('tk.sin_asignar')}</span>}</dd>
              <dt className="text-slate-500">{t('tk.categoria')}</dt>
              <dd>{tk.category ? `${tk.category}${tk.subcategory ? ` › ${tk.subcategory}` : ''}` : <span className="text-slate-400">{t('tk.sin_clasificar')}</span>}</dd>
              <dt className="text-slate-500">{t('tk.campo_app')}</dt>
              <dd>{tk.affectedApp ?? '—'}</dd>
              <dt className="text-slate-500">{t('tk.col_creado')}</dt>
              <dd>{formatDateTime(tk.createdAt)}</dd>
            </dl>
          </Card>
          {tk.can.manage && (tk.statusCategory === 'en_cola' || tk.statusCategory === 'en_curso') && <ActiveControl ticket={tk} />}
          {tk.can.manage && form.data && <TriageCard ticket={tk} form={form.data} />}
          {tk.can.manage && form.data?.statuses && <StatusCard ticket={tk} statuses={form.data.statuses} />}
          {!tk.can.manage && isRequester && tk.statusCategory === 'en_cola' && <CancelCard ticket={tk} />}
          <AttachmentsCard entity="ticket" entityId={tk.id} canUpload={tk.can.reply} canManage={tk.can.manage} help={t('tk.adjuntar_ayuda')} />
          <UsedArticlesCard articles={tk.articles ?? []} />
          <Card variant="aside" title={t('tk.historial')}>
            <ol className="space-y-1 text-sm">
              {tk.history.map((h, i) => (
                <li key={`${h.changedAt}-${i}`} className="flex justify-between gap-2">
                  <span>{h.status}</span>
                  <span className="text-slate-500">{formatDateTime(h.changedAt)}</span>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Quien abre el ticket ya vio lo que avisaban sus notificaciones: se marcan leídas para apagar la campana. */
function useReadTicketNotifications(number: string) {
  const qc = useQueryClient();
  const notifications = useNotifications();
  useEffect(() => {
    const pending = notifications.data?.items.filter((n) => !n.readAt && n.link === `/tickets/${number}`) ?? [];
    if (pending.length === 0) return;
    void Promise.all(pending.map((n) => api(`/api/notificaciones/${n.id}/leida`, { method: 'POST' })))
      .then(() => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }))
      .catch(() => {}); // no es grave: quedan sin leer en la campana
  }, [notifications.data, number, qc]);
}

/** Estado del SLA en palabras: el solicitante también lo ve, sin jerga. */
function SlaCard({ sla }: { sla: Sla }) {
  const { main, detail, late } = describeSla(sla);
  return (
    <Card title={t('tk.sla')}>
      <p className={`text-sm font-medium ${late ? 'text-red-700' : 'text-slate-900'}`}>{main}</p>
      {detail && <p className="mt-0.5 text-xs text-slate-600">{detail}</p>}
      <SlaBar sla={sla} />
    </Card>
  );
}

/** Ticket activo del técnico: tomarlo (si no es de otro) o soltarlo. */
function ActiveControl({ ticket }: { ticket: TicketDetail }) {
  const me = useMe();
  const action = useAction();
  const mine = ticket.assigneeId === me.id;
  if (ticket.activeSince) {
    return (
      <Card>
        <div className="space-y-2 text-sm">
          {action.error && <Alert>{action.error}</Alert>}
          <p>
            <Badge tone="blue">{mine ? t('tk.activo_suyo') : t('tk.activo_de', { nombre: ticket.assignee ?? '—' })}</Badge>
          </p>
          <Button variant="secondary" disabled={action.busy} onClick={() => void action.run(() => api(`/api/tickets/${ticket.number}/activo`, { method: 'DELETE' }), t('tk.soltado'))}>
            {t('tk.soltar')}
          </Button>
        </div>
      </Card>
    );
  }
  if (ticket.assigneeId && !mine) return null; // es de otro técnico: se reasigna desde la clasificación
  return (
    <Card>
      <p className="mb-2 text-sm text-slate-600">{t('tk.tomar_ayuda')}</p>
      <TakeButton number={ticket.number} label={t('tk.tomar_este')} />
    </Card>
  );
}

type TriageFields = { nature: Nature; categoryId: string; subcategoryId: string; priorityId: string; assigneeId: string; affectedAppId: string };

/** Triage de IT: clasificar, priorizar y asignar. Se envía solo lo que cambió (la API completa los valores por defecto). */
function TriageCard({ ticket, form }: { ticket: TicketDetail; form: TicketFormData }) {
  const initial: TriageFields = {
    nature: ticket.nature,
    categoryId: ticket.categoryId ?? '',
    subcategoryId: ticket.subcategoryId ?? '',
    priorityId: ticket.priorityId ?? '',
    assigneeId: ticket.assigneeId ?? '',
    affectedAppId: ticket.affectedAppId ?? '',
  };
  const [v, setV] = useState(initial);
  // Al recargarse el ticket (lo guardado y lo que completó la API, como el responsable por ruteo) se parte de nuevo.
  const [syncedAt, setSyncedAt] = useState(ticket.updatedAt);
  if (syncedAt !== ticket.updatedAt) {
    setSyncedAt(ticket.updatedAt);
    setV(initial);
  }
  const action = useAction();
  const assignable = useQuery({
    queryKey: ['tickets', 'asignables', ticket.organizationId],
    queryFn: () => api<Array<{ id: string; fullName: string }>>(`/api/tickets/asignables/${ticket.organizationId}`),
  });
  const subcategories = (form.subcategories ?? []).filter((s) => s.categoryId === v.categoryId);
  const changes = Object.fromEntries(
    (Object.keys(v) as Array<keyof TriageFields>).filter((k) => v[k] !== initial[k]).map((k) => [k, k === 'nature' ? v[k] : v[k] || null]),
  );
  const set = (patch: Partial<TriageFields>) => setV((prev) => ({ ...prev, ...patch }));

  return (
    <Card title={t('tk.triage')}>
      <div className="space-y-3">
        {action.error && <Alert>{action.error}</Alert>}
        {action.notice && <Alert tone="success">{action.notice}</Alert>}
        <Select id="tr-categoria" label={t('tk.categoria')} value={v.categoryId} onChange={(e) => set({ categoryId: e.target.value, subcategoryId: '' })}>
          <option value="">{t('tk.sin_clasificar')}</option>
          {form.categories?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select id="tr-subcategoria" label={t('tk.subcategoria')} value={v.subcategoryId} disabled={!v.categoryId} onChange={(e) => set({ subcategoryId: e.target.value })}>
          <option value="">{t('tk.elegir')}</option>
          {subcategories.map((s) => (
            <option key={s.id} value={s.id}>
              {s.slaBaseHours ? t('tk.subcategoria_sla', { nombre: s.name, horas: s.slaBaseHours }) : s.name}
            </option>
          ))}
        </Select>
        <Select id="tr-prioridad" label={t('tk.prioridad')} value={v.priorityId} onChange={(e) => set({ priorityId: e.target.value })}>
          <option value="">{t('tk.sin_prioridad')}</option>
          {form.priorities?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Select id="tr-responsable" label={t('tk.responsable')} value={v.assigneeId} onChange={(e) => set({ assigneeId: e.target.value })}>
          <option value="">{t('tk.sin_asignar')}</option>
          {/* El responsable actual sigue en la lista aunque ya no pueda gestionar tickets. */}
          {ticket.assigneeId && !assignable.data?.some((a) => a.id === ticket.assigneeId) && <option value={ticket.assigneeId}>{ticket.assignee ?? ticket.assigneeId}</option>}
          {assignable.data?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.fullName}
            </option>
          ))}
        </Select>
        <Select id="tr-naturaleza" label={t('tk.campo_naturaleza')} value={v.nature} onChange={(e) => set({ nature: e.target.value as Nature })}>
          {NATURES.map((n) => (
            <option key={n} value={n}>
              {natureName(n)}
            </option>
          ))}
        </Select>
        <Select id="tr-app" label={t('tk.campo_app')} value={v.affectedAppId} onChange={(e) => set({ affectedAppId: e.target.value })}>
          <option value="">{t('tk.app_ninguna')}</option>
          {appLabels(form.apps).map((a) => (
            <option key={a.id} value={a.id}>
              {a.label}
            </option>
          ))}
        </Select>
        <Button
          disabled={action.busy || Object.keys(changes).length === 0}
          onClick={() => void action.run(() => api(`/api/tickets/${ticket.number}/triage`, { method: 'PATCH', json: changes }), t('tk.triage_guardado'))}
        >
          {t('comun.guardar')}
        </Button>
      </div>
    </Card>
  );
}

function StatusCard({ ticket, statuses }: { ticket: TicketDetail; statuses: NonNullable<TicketFormData['statuses']> }) {
  const options = statuses.filter((s) => s.id !== ticket.statusId);
  const [statusId, setStatusId] = useState('');
  const action = useAction();
  return (
    <Card title={t('tk.cambiar_estado')}>
      <div className="space-y-3">
        {action.error && <Alert>{action.error}</Alert>}
        {action.notice && <Alert tone="success">{action.notice}</Alert>}
        <Select id="tk-nuevo-estado" label={t('tk.nuevo_estado')} value={statusId} onChange={(e) => setStatusId(e.target.value)}>
          <option value="">{t('tk.elegir')}</option>
          {options.map((s) => (
            <option key={s.id} value={s.id}>
              {s.pausesSla ? t('tk.estado_pausa', { nombre: s.name }) : s.name}
            </option>
          ))}
        </Select>
        <Button
          disabled={action.busy || !statusId}
          onClick={() =>
            void action.run(async () => {
              await api(`/api/tickets/${ticket.number}/estado`, { json: { statusId } });
              setStatusId('');
            }, t('tk.estado_cambiado'))
          }
        >
          {t('tk.cambiar_estado')}
        </Button>
      </div>
    </Card>
  );
}

/** El solicitante puede cancelar su ticket mientras nadie lo tomó. */
function CancelCard({ ticket }: { ticket: TicketDetail }) {
  const [confirming, setConfirming] = useState(false);
  const action = useAction();
  return (
    <Card>
      <div className="space-y-2 text-sm">
        {action.error && <Alert>{action.error}</Alert>}
        {action.notice ? (
          <Alert tone="success">{action.notice}</Alert>
        ) : confirming ? (
          <Alert tone="warning">
            <p>{t('tk.cancelar_confirmar')}</p>
            <div className="mt-2 flex gap-2">
              <Button variant="danger" disabled={action.busy} onClick={() => void action.run(() => api(`/api/tickets/${ticket.number}/cancelar`, { method: 'POST' }), t('tk.cancelado'))}>
                {t('tk.si_cancelar')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('comun.volver')}
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            <p className="text-slate-600">{t('tk.cancelar_ayuda')}</p>
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              {t('tk.cancelar_ticket')}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
